import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import TypingIndicator from "@/components/chat/TypingIndicator";
import { toast } from "@/components/ui/use-toast";
import { generateMaplePTCompletion } from "@/lib/maplept";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import Papa from "papaparse";
import { ensureDir, ensureFile } from "@/OS/utils/filesystem";

type LiteMessage = { role: "user" | "assistant" | "system"; content: string };

const MaplePTQuickChatModal: React.FC = () => {
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<LiteMessage[]>([
    {
      role: "system",
      content:
        "You are MaplePT, a Canadian-built language model. Keep responses concise and helpful for a quick preview.",
    },
  ]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [starterTopic, setStarterTopic] = useState("");
  const [starterOutline, setStarterOutline] = useState("");

  const isAuthenticated = useMemo(() => Boolean(user), [user]);
  const limitReached = !isAuthenticated && userMessageCount >= 3;

  const resetOnOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setMessages([
        {
          role: "system",
          content:
            "You are MaplePT, a Canadian-built language model. Keep responses concise and helpful for a quick preview.",
        },
      ]);
      setUserMessageCount(0);
      setInput("");
      setIsSending(false);
    }
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || limitReached) return;

    const nextMessages: LiteMessage[] = [
      ...messages,
      { role: "user", content: trimmed },
    ];

    setMessages(nextMessages);
    setInput("");
    setIsSending(true);

    try {
      const reply = await generateMaplePTCompletion(
        nextMessages.map((m) => ({ role: m.role, content: m.content })),
        ""
      );

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: reply.content || "…" },
      ]);

      setUserMessageCount((c) => c + 1);
    } catch (err: any) {
      toast({
        title: "Error",
        description:
          typeof err?.message === "string"
            ? err.message
            : "Failed to contact MaplePT.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <Button
        type="button"
        className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-md"
        onClick={() => setOpen(true)}
      >
        Try MaplePT
      </Button>

      {open && (
        <Dialog open={open} onOpenChange={resetOnOpenChange}>
          <DialogContent className="max-w-2xl z-[9999]">
            <DialogHeader>
              <DialogTitle>Try MaplePT</DialogTitle>
              <DialogDescription>
                Ask a quick question. This preview allows up to 3 messages before
                you register.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="max-h-[40vh] overflow-y-auto rounded-md border p-3 bg-muted/30">
                {messages
                  .filter((m) => m.role !== "system")
                  .map((m, idx) => (
                    <div
                      key={idx}
                      className={`mb-3 flex ${
                        m.role === "user"
                          ? "justify-end"
                          : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                          m.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-secondary text-secondary-foreground"
                        }`}
                      >
                        {m.content}
                      </div>
                    </div>
                  ))}

                {isSending && (
                  <div className="flex justify-start">
                    <div className="rounded-lg px-3 py-2">
                      <TypingIndicator />
                    </div>
                  </div>
                )}
              </div>

              <Textarea
                placeholder={
                  limitReached
                    ? "Preview limit reached. Please register to continue."
                    : "Type your question… (Shift+Enter for newline)"
                }
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isSending || limitReached}
              />

              <div className="flex justify-end">
                <Button onClick={handleSend} disabled={isSending || limitReached}>
                  {isSending ? "Sending…" : "Send"}
                </Button>
              </div>

              {!isAuthenticated && limitReached && (
                <div className="rounded-md border bg-accent/30 p-3 flex items-center justify-between">
                  <span className="text-sm">
                    Create your account to keep chatting with MaplePT.
                  </span>
                  <Button asChild>
                    <Link to="/auth">Register</Link>
                  </Button>
                </div>
              )}
            </div>

            <DialogFooter className="sm:justify-between">
              <div className="text-xs text-muted-foreground">
                Powered by MaplePT Mini — Open, Canadian, sovereign AI.
              </div>
              <Button asChild variant="outline">
                <a
                  href="https://doi.org/10.36227/techrxiv.176162061.19169845/v1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read the Paper
                </a>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default MaplePTQuickChatModal;
